/**
 * Shared credit error parsing utility.
 *
 * Provides a single function that detects insufficient-credits errors
 * regardless of shape: typed object, keyword string, or JSON embedded in a string.
 */

import { CREDIT_COSTS } from './constants';

export interface CreditError {
  current_credits: number;
  required_credits: number;
  message: string;
}

/**
 * Parse an unknown error into a CreditError if it represents an insufficient-credits failure.
 * Returns null for all other error types.
 *
 * Handles three shapes:
 * 1. Object with `type === 'insufficient_credits'`
 * 2. String containing "insufficient credits" / "not enough credits"
 * 3. JSON `{"type":"insufficient_credits",...}` embedded in a string
 */
export function parseCreditError(err: unknown): CreditError | null {
  // Shape 1: Object with type === 'insufficient_credits'
  if (err && typeof err === 'object' && 'type' in err) {
    const obj = err as Record<string, unknown>;
    if (obj.type === 'insufficient_credits') {
      return {
        current_credits: typeof obj.current_credits === 'number' ? obj.current_credits : 0,
        required_credits: typeof obj.required_credits === 'number' ? obj.required_credits : CREDIT_COSTS.VIDEO_GENERATION,
        message: typeof obj.message === 'string' ? obj.message : 'Insufficient credits for video generation',
      };
    }
  }

  // Shape 2 & 3: String containing credit-related keywords
  if (typeof err === 'string') {
    const lower = err.toLowerCase();
    if (
      lower.includes('insufficient credits') ||
      lower.includes('not enough credits') ||
      (lower.includes('need') && lower.includes('credits'))
    ) {
      // Shape 3: Try to extract JSON embedded in string
      try {
        const match = err.match(/\{.*"type"\s*:\s*"insufficient_credits".*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          return {
            current_credits: parsed.current_credits ?? 0,
            required_credits: parsed.required_credits ?? CREDIT_COSTS.VIDEO_GENERATION,
            message: parsed.message || err,
          };
        }
      } catch {
        // Fall through to generic credit error
      }
      return {
        current_credits: 0,
        required_credits: CREDIT_COSTS.VIDEO_GENERATION,
        message: err,
      };
    }
  }

  return null;
}
